import { ServiceDefinitionWithItsRelatedCombos } from "@/Appointments/Models/Scheduling/ComboServiceModel";
import { ServiceDefinitionContext } from "@/Appointments/Models/Scheduling/CustomServiceModel";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { ServiceComboDefinitionModel } from "@/Common/Models/Services/ServiceComboDefinitionModel";
import { ServiceModel } from "@/Common/Models/Services/ServiceModel";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";

interface ISearchComboServiceComponentProps {
    selectedComboServiceList: Immutable.List<ServiceDefinitionWithItsRelatedCombos>;
    selectedEmployeeContactId?: string;
    selectedSiteId: string
    comboServiceListSelectionChangeOperation?: number;
    isFromOpenSchedule: boolean;
    isSingleComboServiceSelect?: boolean;
    isServiceOnlySelect?: boolean;
}

class SearchComboServiceComponentState {
    public ComboServiceForEmployeeAndSite: Immutable.List<ServiceDefinitionWithItsRelatedCombos>;
    public ComboServiceListFiltered: Immutable.List<ServiceDefinitionWithItsRelatedCombos>;
    public SelectedComboServiceList: Immutable.List<ServiceDefinitionWithItsRelatedCombos>;
    public ComboPopupSearchText: string;
    public IsAllSelected: boolean;

    constructor(s?: SearchComboServiceComponentState) {
        if (s != null) {
            this.ComboServiceForEmployeeAndSite = s.ComboServiceForEmployeeAndSite;
            this.ComboServiceListFiltered = s.ComboServiceListFiltered;
            this.SelectedComboServiceList = s.SelectedComboServiceList;
            this.IsAllSelected = s.IsAllSelected;
            this.ComboPopupSearchText = s.ComboPopupSearchText;
        }
        else {
            this.ComboServiceForEmployeeAndSite = [].toImmutable();
            this.ComboServiceListFiltered = [].toImmutable();
            this.SelectedComboServiceList = [].toImmutable();
        }
    }
}

class SearchComboServiceComponent extends BaseReactComponent<ISearchComboServiceComponentProps, SearchComboServiceComponentState> {
    constructor(props: ISearchComboServiceComponentProps) {
        super(props);

        const initialState = new SearchComboServiceComponentState();
        initialState.ComboPopupSearchText = "";
        initialState.SelectedComboServiceList = this.props.selectedComboServiceList ?? [].toImmutable();

        if (this.props.isFromOpenSchedule === true) {
            const combosForSite = cacheStore.ServiceDefinitionComboCache.filter(x => x.Model.SiteId == this.props.selectedSiteId);
            let employeeServices: ServiceModel[] = [];
            employeeServices = cacheStore.GetEmployeeByContactIdOrDefault(this.props.selectedEmployeeContactId).ServicesOffered.filter(x => !x.ServiceDefinitionModel.IsAdHoc && x.ServiceDefinitionModel.Model.SiteId === this.props.selectedSiteId);

            const employeeServiceList = this.filteringSelectedService(employeeServices, initialState.SelectedComboServiceList, combosForSite);
            initialState.ComboServiceForEmployeeAndSite = employeeServiceList;
            initialState.ComboServiceListFiltered = employeeServiceList;
        }
        else {
            const combos = cacheStore.ServiceDefinitionComboCache;
            const employeeServices: ServiceModel[] = [];

            cacheStore.EmployeeCache.forEach(u => {
                u.ServicesOffered.filter(v => !v.ServiceDefinitionModel.IsAdHoc && v.ServiceDefinitionModel.Model.SiteId === this.props.selectedSiteId && !employeeServices.some(y => y.ServiceDefinitionModel.Model.Id === v.ServiceDefinitionModel.Model.Id)).forEach(o => employeeServices.push(o));
            });

            const employeeServiceList = this.filteringSelectedService(employeeServices, initialState.SelectedComboServiceList, combos);
            initialState.ComboServiceForEmployeeAndSite = employeeServiceList;
            initialState.ComboServiceListFiltered = employeeServiceList;
        }

        this.state = initialState;
    }

    private filteringSelectedService(
        allServices: ServiceModel[],
        selectedServiceCombo: Immutable.List<ServiceDefinitionWithItsRelatedCombos>,
        combosForSite: Immutable.Iterable<number, ServiceComboDefinitionModel>
    ): Immutable.List<ServiceDefinitionWithItsRelatedCombos> {
        let employeeServiceList = allServices.map(x => {
            let selectedCombo = selectedServiceCombo.filter(m => m.ServiceDefinitionId === x.ServiceDefinitionModel.Model.Id).first();

            let comboServiceModel = new ServiceDefinitionWithItsRelatedCombos();
            comboServiceModel.Name = x.GetName();
            comboServiceModel.FullName = x.GetName() + " ( " + x.ServiceDefinitionModel.GetDefaultDurationMinutes() + " min. ) " + cacheStore.GetLocalizedValueByLookupId(x.ServiceDefinitionModel.Model.SpecializationLookupId);
            comboServiceModel.NameWithDuration = x.GetName() + " (" + x.ServiceDefinitionModel.GetDefaultDurationMinutes() + " min.) ";
            comboServiceModel.ServiceDefinitionId = x.ServiceDefinitionModel.Model.Id;
            comboServiceModel.ServiceDefinitionDuration = x.ServiceDefinitionModel.Model.DefaultDuration;
            comboServiceModel.ServiceDefinitionSpecialityId = x.ServiceDefinitionModel.Model.SpecializationLookupId;
            comboServiceModel.LicenceOrganisationSpecialisationId = x.ServiceDefinitionModel.Model.LicenceOrganisationSpecialisationId;
            comboServiceModel.IsTeleconsultation = x.ServiceDefinitionModel.Model?.IsTeleconsultation;
            comboServiceModel.GroupMax = selectedCombo ? selectedCombo.GroupMax : 0;

            // StandAlone Context
            let standAloneContext = new ServiceDefinitionContext();
            standAloneContext.Name = x.GetName();
            standAloneContext.IsCombo = false;
            standAloneContext.IsOnline = x.ServiceDefinitionModel.Model.IsAvailableOnline;
            standAloneContext.TargetedClient = x.ServiceDefinitionModel.Model.OnlineClienenteleCible;
            standAloneContext.IsTeleconsultation = x.ServiceDefinitionModel.Model.IsTeleconsultation;

            if (selectedCombo) {
                let sameNameContext = selectedCombo.RelatedServiceDefinitionContexts.filter(context => context.Name === selectedCombo.Name).firstOrDefault();
                if (sameNameContext && sameNameContext.Checked) {
                    standAloneContext = sameNameContext;
                }
            }

            comboServiceModel.RelatedServiceDefinitionContexts.push(standAloneContext);

            if (this.props.isServiceOnlySelect)
                return comboServiceModel;

            // Combos Contexts
            comboServiceModel.HasCombo = false;
            let comboFilteredList = combosForSite.filter(n => n.ServiceDefinitions.some(y => y.Model.Id === x.ServiceDefinitionModel.Model.Id));
            comboFilteredList.forEach(m => {
                let comboContext = selectedCombo ? selectedCombo.RelatedServiceDefinitionContexts.filter(j => j.ComboId === m.Model.ComboId).firstOrDefault() : null;

                if (!comboContext) {
                    comboContext = new ServiceDefinitionContext();
                    comboContext.IsCombo = true;
                    comboContext.Name = m.Name;
                    comboContext.ComboId = m.Model.ComboId;
                    comboContext.IsOnline = m.Model.IsAvailableOnline;
                    comboContext.TargetedClient = m.Model.OnlineClienenteleCible;
                }
                else {
                    comboContext.Checked = true;
                }

                comboContext.IsTeleconsultation = m.Model.IsTeleconsultation;
                comboServiceModel.RelatedServiceDefinitionContexts.push(comboContext);
            });
            comboServiceModel.HasCombo = comboFilteredList.size > 0;

            comboServiceModel.RelatedServiceDefinitionContexts.sortByProperty(l => l.IsCombo);
            return comboServiceModel;
        });

        return employeeServiceList.toImmutable();
    }

    public componentDidUpdate(prevProps: Readonly<ISearchComboServiceComponentProps>): void {
        if (this.props !== prevProps) {
            this.mutateState(s => s.SelectedComboServiceList = this.props.selectedComboServiceList);
        }
    }

    public render(): JSX.Element {
        const isMultiSelect = !this.props.isSingleComboServiceSelect;

        return (
            <div>
                <TextboxControl value={this.state.ComboPopupSearchText} onChange={e => this.onComboPopupTextChange(e)} placeholder={cacheStore.GetString("SearchLabel")} />

                {isMultiSelect && (
                    <div className="checkbox" style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <label>
                            <input type="checkbox" id="checkBoxAllSelected" checked={this.state.IsAllSelected} onChange={e => this.onIsAllSelectedCheckChange(e.target.checked)} />
                            {cacheStore.GetString("SelectAllServiceComboLabel")}
                        </label>
                    </div>
                )}

                <div style={{ height: "400px", overflowY: "auto" }}>
                    {this.state.ComboServiceListFiltered
                        ?.filter(x => x.Visible)
                        .sortBy(p => p.Name)
                        .map((serviceDefinition: ServiceDefinitionWithItsRelatedCombos) => {
                            ServiceDefinitionWithItsRelatedCombos.SortRelatedServiceDefinitions(serviceDefinition);

                            const serviceContext = serviceDefinition.RelatedServiceDefinitionContexts.filter(p => !p.IsCombo)[0];
                            const relatedCombos = serviceDefinition.RelatedServiceDefinitionContexts.filter(p => p.IsCombo);

                            const checkboxOrRadio = isMultiSelect ? "checkbox" : "radio";

                            return (
                                <div className={checkboxOrRadio} style={{ marginLeft: "5px", marginTop: "5px" }} key={"item_" + serviceDefinition.ServiceDefinitionId}>
                                    <label style={{ width: "100%" }}>
                                        <input type={checkboxOrRadio} radioGroup="SearchComboService" id={serviceDefinition.Id} checked={serviceContext.Checked} onChange={e => this.onServiceCheckChange(serviceDefinition.Id, e.target.checked)} />
                                        <div className="flex-container space-between">
                                            <div>
                                                <span style={{ paddingLeft: "0px" }}>
                                                    <strong>{serviceDefinition.Name}</strong> ({cacheStore.GetLocalizedValueByLookupId(serviceDefinition.ServiceDefinitionSpecialityId)})
                                                </span>
                                            </div>
                                            <div className="text-right flex-container flex-end" style={{ width: "115px", paddingLeft: "5px" }}>
                                                <span style={{ width: "20px" }}>
                                                    {serviceDefinition.IsTeleconsultation && <i className="fi-video col-lg-icon" />}
                                                </span>
                                                <span style={{ width: "55px" }}>{serviceDefinition.ServiceDefinitionDuration / 600000000} min.</span>
                                            </div>
                                        </div>
                                    </label>
                                    {isMultiSelect && relatedCombos.length > 1 && (
                                        <div className="checkbox" key="subItem_AllCombo" style={{ marginLeft: "15px", marginTop: "0px", marginBottom: "0px" }}>
                                            <label>
                                                <input id="AllCombo" type="checkbox" checked={relatedCombos.every(x => x.Checked)} onChange={e => this.onAllComboCheckChange(serviceDefinition.Id, e.target.checked)} />
                                                {cacheStore.GetString("AllLabel")}
                                            </label>
                                        </div>
                                    )}
                                    {relatedCombos
                                        .filter(p => p.Visible)
                                        .map((combo: ServiceDefinitionContext) => (
                                            <div className={checkboxOrRadio} key={"subItem_" + combo.Id} style={{ marginLeft: "15px", marginTop: "0px", marginBottom: "0px" }}>
                                                <label>
                                                    <input id={combo.Id} type={checkboxOrRadio} radioGroup="SearchComboService" checked={combo.Checked} onChange={e => this.onComboCheckChange(serviceDefinition.Id, combo.Id, e.target.checked)} />
                                                    {combo.Name}
                                                </label>
                                            </div>
                                        ))}
                                </div>
                            );
                        })}
                </div>
            </div>
        );
    }

    public shouldComponentUpdate(): boolean {
        return true;
    }

    private onIsAllSelectedCheckChange(checked: boolean): void {
        this.onServiceCheckChange("AllService", checked);
    }

    private onServiceCheckChange(serviceId: string, checked: boolean): void {
        const isMultiSelect = !this.props.isSingleComboServiceSelect;

        this.mutateState(
            s => {
                s.ComboServiceListFiltered = s.ComboServiceListFiltered
                    .map(service => {
                        if (service.Id !== serviceId && serviceId !== "AllService") {
                            if (isMultiSelect)
                                return service;

                            service.RelatedServiceDefinitionContexts = service.RelatedServiceDefinitionContexts
                                .map(context => {
                                    context.Checked = false;

                                    return context;
                                });

                            return service;
                        }

                        service.RelatedServiceDefinitionContexts = service.RelatedServiceDefinitionContexts
                            .map(context => {
                                if (isMultiSelect)
                                    context.Checked = checked;
                                else
                                    context.Checked = !context.IsCombo && checked;

                                return context;
                            });

                        return service;
                    })
                    .toList();
            },
            () => this.updateSelectedComboServiceListModel()
        );
    }

    private onAllComboCheckChange(serviceId: string, checked: boolean): void {
        this.onComboCheckChange(serviceId, "AllCombo", checked);
    }

    private onComboCheckChange(serviceId: string, comboId: string, checked: boolean): void {
        const isMultiSelect = !this.props.isSingleComboServiceSelect;

        this.mutateState(
            s => {
                s.ComboServiceListFiltered = s.ComboServiceListFiltered
                    .map(service => {
                        if (service.Id !== serviceId) {
                            if (isMultiSelect)
                                return service;

                            service.RelatedServiceDefinitionContexts = service.RelatedServiceDefinitionContexts
                                .map(context => {
                                    context.Checked = false;

                                    return context;
                                });

                            return service;
                        }

                        service.RelatedServiceDefinitionContexts = service.RelatedServiceDefinitionContexts
                            .map(context => {
                                if (context.Id === comboId || (context.IsCombo && comboId === "AllCombo"))
                                    context.Checked = checked;
                                else if (!isMultiSelect)
                                    context.Checked = false;

                                return context;
                            });

                        return service;
                    })
                    .toList();
            },
            () => this.updateSelectedComboServiceListModel()
        );
    }

    private onComboPopupTextChange(searchText: string): void {
        this.mutateState(s => {
            s.ComboPopupSearchText = searchText;

            // reset all visibility
            s.ComboServiceForEmployeeAndSite = this.state.ComboServiceForEmployeeAndSite
                .map(x => {
                    x.Visible = true;
                    x.RelatedServiceDefinitionContexts.forEach(z => { z.Visible = true; });
                    return x;
                }).toList();

            // default values if no search terms
            if (IsNullOrWhiteSpace(searchText)) {
                s.ComboServiceListFiltered = s.ComboServiceForEmployeeAndSite;
            }
            else {
                let filteredsearchableStandalones = s.ComboServiceForEmployeeAndSite.toArray()
                    .filterSearchAdvanced(
                        searchText,
                        (IsMatchingTextFct, item) => IsMatchingTextFct(item.FullName) || item.RelatedServiceDefinitionContexts.some(subItem => IsMatchingTextFct(subItem.Name))
                    );

                let tmpList = s.ComboServiceListFiltered.map(x => {
                    x.Visible = filteredsearchableStandalones.some(y => y.Id == x.Id);

                    return x;
                });

                s.ComboServiceListFiltered = tmpList.sortBy(p => p.Name).toList();
            }
        });
    }

    private updateSelectedComboServiceListModel(): void {
        // a item could be selected, but not shown in currentview (because of search term filtering)
        const selectedComboService = this.state.ComboServiceForEmployeeAndSite
            .filter(service => service.RelatedServiceDefinitionContexts.some(combo => combo.Checked))
            .map(hid => {
                let hiddenClone = ServiceDefinitionWithItsRelatedCombos.Clone(hid);
                hiddenClone.RelatedServiceDefinitionContexts = hiddenClone.RelatedServiceDefinitionContexts.filter(rel => rel.Checked == true);
                return hiddenClone;
            });

        this.mutateState(
            s => s.SelectedComboServiceList = selectedComboService.toList(),
            () => {
                if (!this.props.comboServiceListSelectionChangeOperation)
                    return;

                let op = new LocalEvent(this.props.comboServiceListSelectionChangeOperation);

                op.AddItem("selectedItems", this.state.SelectedComboServiceList);

                LocalEventDispatcher.dispatch(op);
            }
        );
    }
}

export { SearchComboServiceComponent };
